import { type TimestampUTC } from '~/types/common';

import { type AcceptItem, type AssetChain } from '~/data/delivery';
import { type UserBulkAdmin } from '~/data/user';

import { entries, filter, find, flatMap, pipe } from '@vestigas/utils';

import { type DeliveryNoteAdmin } from '../types';

import { getCreatorId } from './getCreatorId';

/**
 * Combines the accept and partialAccept states into a single state.
 * @param accept - The accept state.
 * @param partialAccept - The partial accept state.
 * @returns The composite accept state.
 */
export const getCompositeAcceptState = (
  accept: AcceptItem['accept'],
  partialAccept: AcceptItem['partialAccept'],
): 'accepted' | 'partiallyAccepted' | 'rejected' | 'open' | unknown => {
  let compositeAcceptState = accept === 'valid' ? 'accepted' : accept;

  if (partialAccept?.valid) {
    return 'partiallyAccepted';
  }

  compositeAcceptState ||=
    Boolean(partialAccept?.declined) || Boolean(partialAccept?.wrong)
      ? 'rejected'
      : 'open';

  return compositeAcceptState;
};

export const getComparedTime = (dateTime: TimestampUTC) => {
  // Only compare the timestamps up to a certain precision, because the values might differ in milliseconds.
  // This is a naive shortcut and might fail if the difference in milliseconds
  // leads to just switching the second, minute, hour and so on...
  // The robust approach would be to check if the difference is less than a certain threshold.
  return typeof dateTime === 'string' ? dateTime.slice(0, 16) : dateTime; // 16 digits -> same minute
};

/**
 * Function to find the related asset chain for a given accepting party and accepted items.
 * @param assetChains - The asset chains to search in.
 * @param acceptedItems - The accepted items to search for.
 * @param acceptingParty - The accepting party to search for.
 * @returns The related asset chain.
 */
export const findRelatedAssetChain = (
  assetChains: AssetChain[] = [],
  acceptedItems: AcceptItem[],
  acceptingParty = 'recipient',
) => {
  if (!assetChains?.length || !acceptedItems?.length || !acceptingParty) {
    return undefined;
  }

  const relatedAssetChain = find(
    assetChains,
    (assetChain) =>
      assetChain?.signatureRole
        ?.toLowerCase()
        ?.includes(acceptingParty.toLowerCase()) && // Match both regular and on behalf roles
      getComparedTime(assetChain.createdOn) ===
        getComparedTime(acceptedItems[0]?.dateTime ?? ''),
  );

  return relatedAssetChain;
};

/**
 * Returns the processed and enriched accepted line items for a given party of the given delivery note.
 * @param acceptedItems - All accepted items to get the accepted items for the given party from.
 * @param acceptingParty - The party to get the accepted items for.
 * @param assetChains - The asset chains to link the accepted items to.
 * @param users - The users to link the accepted items to.
 * @returns The accepted items for the given party.
 */
export const getEnrichedAcceptedItems = (
  acceptedItems: AcceptItem[],
  acceptingParty = 'recipient',
  assetChain: AssetChain | undefined,
  users: any[] = [],
) => {
  if (!acceptedItems?.length || !acceptingParty || !assetChain) {
    return [];
  }

  const acceptedItemsForParty =
    acceptedItems.map(
      ({
        accept,
        attachments,
        dateTime,
        onBehalf,
        partialAccept,
        path,
        reason,
      }) => {
        const compositeAcceptState = getCompositeAcceptState(
          accept,
          partialAccept,
        );

        const userId = getCreatorId(assetChain.createdBy);
        const user = find(users, ({ id }) => id === userId);

        return {
          accept,
          acceptingParty,
          assetChainId: assetChain.id,
          attachments,
          compositeAcceptState,
          dateTime: dateTime ?? assetChain.createdOn, // fall back to createdOn; assetChain acceptItems have no dateTime
          lineItemIndex: path?.at(-1),
          onBehalf,
          partialAccept: {
            declined: partialAccept?.declined,
            valid: partialAccept?.valid,
            wrong: partialAccept?.wrong,
          },
          reason: reason?.trim() || undefined,
          user: user ? { id: userId, ...user } : { id: userId },
        };
      },
    ) ?? [];

  return acceptedItemsForParty;
};

/**
 * Returns the accepted line items for each party of the given delivery note.
 * @param deliveryNote - The delivery note to get the accepted items from.
 * @param assetChains - The asset chains to link the accepted items to.
 * @param users - The users to link the accepted items to.
 * @returns The accepted items for each party.
 */
export const getAcceptedItems = (
  deliveryNote: DeliveryNoteAdmin,
  assetChains: AssetChain[] = [],
  users: UserBulkAdmin[] = [],
) => {
  if (!deliveryNote?.acceptStates?.acceptItems) {
    return [];
  }

  const acceptedItems = pipe(
    deliveryNote.acceptStates.acceptItems,
    entries,
    flatMap(([acceptingParty, acceptedItems]) => {
      const assetChain = findRelatedAssetChain(
        assetChains,
        acceptedItems,
        acceptingParty,
      );

      return getEnrichedAcceptedItems(
        acceptedItems,
        acceptingParty,
        assetChain,
        users,
      );
    }),
    filter(Boolean),
  );

  return acceptedItems;
};
